/**
 * Which document the Document view shows: the report as it stands (its
 * latest version — what the report page shows), one of the workbench's Word
 * drafts (one a person edited says so), an earlier version, or a partner's
 * Word return — newest first, each draft and return with what the reviewer
 * left open on it — or the partner's form as the workbench pre-filled it
 * (the case's own, and each partner's copy).
 */

import { Select } from '@mantine/core';

import { prefillCopies, type WorkbenchDocument, type WorkbenchPrefill } from '@/types/workbench';

/** The picker's value for the report as it stands. */
export const LATEST = 'latest';
/** A pre-fill's value in the picker: this, then its id. */
export const PREFILL = 'prefill:';

/** A document's moment as the picker writes it, in the viewer's zone. */
export function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ORIGINS: Record<string, string> = {
  workbench_draft: 'released from a draft',
  direct_edit: 'edited',
  partner_return: "the partner's return",
  feedback: 'revised on feedback',
};

function openIssues(doc: WorkbenchDocument): string | null {
  const open = (doc.review?.openErrors ?? 0) + (doc.review?.openWarnings ?? 0);
  return open > 0 ? `${open} open issue${open === 1 ? '' : 's'}` : null;
}

export function documentLabel(doc: WorkbenchDocument): string {
  if (doc.kind === 'version') {
    const origin = doc.origin ? ORIGINS[doc.origin] : undefined;
    return `Version ${doc.version}${origin ? ` — ${origin}` : ''}`;
  }
  if (doc.kind === 'return') {
    return [
      `${doc.filename} · ${when(doc.createdAt)}`,
      doc.status === 'superseded' ? 'resubmitted since' : null,
      openIssues(doc),
    ]
      .filter(Boolean)
      .join(' · ');
  }
  const parts = [`Draft · ${when(doc.createdAt)}`];
  if (doc.author) {
    parts.push('edited');
  } else if (doc.revision) {
    parts.push('revised');
  }
  if (doc.status === 'released' || doc.status === 'superseded') {
    parts.push(doc.status);
  }
  const open = openIssues(doc);
  if (open) {
    parts.push(open);
  }
  return parts.join(' · ');
}

export function prefillLabel(prefill: WorkbenchPrefill): string {
  const copies = prefillCopies(prefill);
  const who =
    prefill.scope === 'case'
      ? 'Pre-filled form'
      : `Partner's copy${prefill.npRequestId !== null ? ` (request ${prefill.npRequestId})` : ''}`;
  const state =
    prefill.scope === 'case'
      ? null
      : prefill.status === 'delivered'
        ? 'delivered'
        : prefill.deliveryError
          ? `not delivered — ${prefill.deliveryError}`
          : 'not delivered';
  return [
    `${who} · ${when(prefill.createdAt)}`,
    copies > 0 ? `${copies} ${copies === 1 ? 'table' : 'tables'}` : null,
    prefill.status === 'superseded' ? 'superseded' : state,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface DocumentPickerProps {
  documents: WorkbenchDocument[];
  /** The partner's form as pre-filled, live builds only. */
  prefills?: WorkbenchPrefill[];
  /** The report's latest version, if it has one. */
  latestVersion: number | null;
  /** `LATEST`, or a draft's or an earlier version's id. */
  value: string;
  onChange: (value: string) => void;
}

export function DocumentPicker({
  documents,
  prefills = [],
  latestVersion,
  value,
  onChange,
}: Readonly<DocumentPickerProps>) {
  const drafts = documents.filter((d) => d.kind === 'draft');
  const forms = prefills.filter((p) => p.status !== 'superseded');
  const earlier = documents.filter(
    (d) => d.kind === 'version' && d.docx && d.version !== latestVersion
  );
  const returns = documents.filter((d) => d.kind === 'return');
  const data = [
    ...(latestVersion !== null
      ? [{ group: 'The report', items: [{ value: LATEST, label: `Version ${latestVersion}` }] }]
      : []),
    ...(drafts.length > 0
      ? [
          {
            group: 'Workbench drafts',
            items: drafts.map((d) => ({ value: d.id, label: documentLabel(d) })),
          },
        ]
      : []),
    ...(earlier.length > 0
      ? [
          {
            group: 'Earlier versions',
            items: earlier.map((d) => ({ value: d.id, label: documentLabel(d) })),
          },
        ]
      : []),
    ...(returns.length > 0
      ? [
          {
            group: "Partners' returns",
            items: returns.map((d) => ({ value: d.id, label: documentLabel(d) })),
          },
        ]
      : []),
    ...(forms.length > 0
      ? [
          {
            group: "Partner's form",
            items: forms.map((p) => ({ value: `${PREFILL}${p.id}`, label: prefillLabel(p) })),
          },
        ]
      : []),
  ];
  return (
    <Select
      size="xs"
      w={360}
      aria-label="Document"
      data={data}
      value={value}
      onChange={(next) => next && onChange(next)}
      allowDeselect={false}
      data-testid="document-picker"
    />
  );
}
